import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  CircularProgress,
  Dialog,
  DialogContent,
  IconButton,
  ImageList,
  ImageListItem,
} from "@mui/material";
import {
  Close as CloseIcon,
  BrokenImage as BrokenImageIcon,
} from "@mui/icons-material";
import axios from "axios";
import { ImageContent } from "src/views/assistant/types";
import { nuclearn_api_url } from "src/App";

type ChatMessageImagesProps = {
  images?: ImageContent[];
  isEditing?: boolean;
  onRemoveImage?: (index: number) => void;
};

type ImageLoadState = {
  src: string;
  loading: boolean;
  error: boolean;
};

/**
 * Component to display VLM images in a chat message.
 * Supports images from:
 * - Base64 data (for immediate display)
 * - Artifact URLs (fetched via axios with auth, converted to blob)
 *
 * Images are clickable to view in a larger modal.
 */
const ChatMessageImages: React.FC<ChatMessageImagesProps> = ({
  images,
  isEditing = false,
  onRemoveImage,
}) => {
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [imageStates, setImageStates] = useState<
    Record<number, ImageLoadState>
  >({});

  // Generate a stable key for each image based on its content
  const imageKeys = useMemo(() => {
    return (images || []).map((img, idx) => {
      if (img.data) return `data-${idx}`;
      if (img.artifact_id) return `artifact-${img.artifact_id}`;
      if (img.url) return `url-${img.url}`;
      return `unknown-${idx}`;
    });
  }, [images]);

  // Fetch artifact images via axios (with auth) and convert to blob URLs
  useEffect(() => {
    if (!images || images.length === 0) return;

    const fetchArtifactImages = async () => {
      const newStates: Record<number, ImageLoadState> = {};

      for (let i = 0; i < images.length; i++) {
        const image = images[i];

        // Base64 data - use directly
        if (image.data) {
          const mediaType = image.media_type || "image/png";
          newStates[i] = {
            src: `data:${mediaType};base64,${image.data}`,
            loading: false,
            error: false,
          };
          continue;
        }

        // Artifact URL - fetch via axios with auth
        if (image.url) {
          const fullUrl = image.url.startsWith("/")
            ? `${nuclearn_api_url}${image.url}`
            : image.url;

          // Check if we already have this image loaded
          const existingState = imageStates[i];
          if (existingState && !existingState.error && existingState.src) {
            newStates[i] = existingState;
            continue;
          }

          // Set loading state
          newStates[i] = { src: "", loading: true, error: false };
          setImageStates((prev) => ({ ...prev, [i]: newStates[i] }));

          try {
            const response = await axios.get(fullUrl, {
              responseType: "blob",
            });

            // Create blob URL
            const blobUrl = URL.createObjectURL(response.data);
            newStates[i] = { src: blobUrl, loading: false, error: false };
          } catch (error) {
            console.error("Failed to load artifact image:", error);
            newStates[i] = { src: "", loading: false, error: true };
          }
        } else {
          // No source available
          newStates[i] = { src: "", loading: false, error: true };
        }
      }

      setImageStates(newStates);
    };

    fetchArtifactImages();

    // Cleanup blob URLs on unmount
    return () => {
      Object.values(imageStates).forEach((state) => {
        if (state.src && state.src.startsWith("blob:")) {
          URL.revokeObjectURL(state.src);
        }
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [imageKeys.join(",")]);

  if (!images || images.length === 0) {
    return null;
  }

  /**
   * Get the image source from pre-loaded state
   */
  const getImageSrc = (index: number): string => {
    return imageStates[index]?.src || "";
  };

  const isLoading = (index: number): boolean => {
    return imageStates[index]?.loading ?? true;
  };

  const hasError = (index: number): boolean => {
    return imageStates[index]?.error ?? false;
  };

  const handleImageClick = (src: string) => {
    setSelectedImage(src);
  };

  const handleClose = () => {
    setSelectedImage(null);
  };

  const imageStyle: React.CSSProperties = {
    maxWidth: "100%",
    maxHeight: "200px",
    borderRadius: "8px",
    objectFit: "contain",
    cursor: "pointer",
    border: "1px solid rgba(0, 0, 0, 0.1)",
    transition: "transform 0.2s, box-shadow 0.2s",
  };

  // Render a single image item with loading/error states
  const renderImageItem = (index: number, maxHeight: string = "200px") => {
    const src = getImageSrc(index);
    const loading = isLoading(index);
    const error = hasError(index);

    // Wrapper with remove button for edit mode
    const wrapWithRemoveButton = (content: React.ReactNode) => {
      if (!isEditing || !onRemoveImage) {
        return content;
      }
      return (
        <Box
          data-testid={`vlm-image-wrapper-${index}`}
          sx={{
            position: "relative",
            display: "inline-block",
            "&:hover .remove-button": {
              opacity: 1,
            },
          }}
        >
          {content}
          <IconButton
            className="remove-button"
            size="small"
            data-testid={`vlm-image-remove-btn-${index}`}
            onClick={(e) => {
              e.stopPropagation();
              onRemoveImage(index);
            }}
            sx={{
              position: "absolute",
              top: 4,
              right: 4,
              bgcolor: "rgba(0, 0, 0, 0.6)",
              color: "white",
              opacity: 0,
              transition: "opacity 0.2s",
              padding: "2px",
              "&:hover": {
                bgcolor: "rgba(0, 0, 0, 0.8)",
              },
            }}
          >
            <CloseIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Box>
      );
    };

    if (loading) {
      return wrapWithRemoveButton(
        <Box
          data-testid={`vlm-image-loading-${index}`}
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: 150,
            height: 100,
            backgroundColor: "rgba(0,0,0,0.05)",
            borderRadius: "8px",
            border: "1px solid rgba(0,0,0,0.1)",
          }}
        >
          <CircularProgress size={24} />
        </Box>,
      );
    }

    if (error || !src) {
      return wrapWithRemoveButton(
        <Box
          data-testid={`vlm-image-error-${index}`}
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: 150,
            height: 100,
            backgroundColor: "rgba(0,0,0,0.05)",
            borderRadius: "8px",
            border: "1px solid rgba(0,0,0,0.1)",
            color: "text.secondary",
          }}
        >
          <BrokenImageIcon />
        </Box>,
      );
    }

    return wrapWithRemoveButton(
      <img
        data-testid={`vlm-image-${index}`}
        src={src}
        alt={`Attached image ${index + 1}`}
        onClick={() => !isEditing && handleImageClick(src)}
        style={{
          ...imageStyle,
          maxHeight,
          cursor: isEditing ? "default" : "pointer",
        }}
      />,
    );
  };

  // Single image - display with thumbnail
  if (images.length === 1) {
    return (
      <>
        <Box
          data-testid="vlm-images-container"
          sx={{
            mt: 1.5,
            mb: 0.5,
            display: "inline-block",
            "&:hover img": {
              transform: "scale(1.02)",
              boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
            },
          }}
        >
          {renderImageItem(0, "200px")}
        </Box>

        {/* Full-size image modal */}
        <Dialog
          data-testid="vlm-image-modal"
          open={!!selectedImage}
          onClose={handleClose}
          maxWidth="lg"
          PaperProps={{
            sx: {
              backgroundColor: "transparent",
              boxShadow: "none",
            },
          }}
        >
          <IconButton
            data-testid="vlm-image-modal-close"
            onClick={handleClose}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
              color: "white",
              backgroundColor: "rgba(0,0,0,0.5)",
              "&:hover": { backgroundColor: "rgba(0,0,0,0.7)" },
            }}
          >
            <CloseIcon />
          </IconButton>
          <DialogContent sx={{ p: 0, overflow: "hidden" }}>
            {selectedImage && (
              <img
                data-testid="vlm-image-modal-fullsize"
                src={selectedImage}
                alt="Full size"
                style={{
                  maxWidth: "90vw",
                  maxHeight: "90vh",
                  objectFit: "contain",
                }}
              />
            )}
          </DialogContent>
        </Dialog>
      </>
    );
  }

  // Multiple images - display in a grid
  return (
    <>
      <Box data-testid="vlm-images-container" sx={{ mt: 1.5, mb: 0.5 }}>
        <ImageList
          data-testid="vlm-images-grid"
          cols={Math.min(images.length, 3)}
          gap={8}
        >
          {images.map((_, index) => (
            <ImageListItem
              key={index}
              data-testid={`vlm-image-item-${index}`}
              sx={{
                "&:hover img": {
                  transform: "scale(1.02)",
                  boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                },
              }}
            >
              {renderImageItem(index, "150px")}
            </ImageListItem>
          ))}
        </ImageList>
      </Box>

      {/* Full-size image modal */}
      <Dialog
        data-testid="vlm-image-modal"
        open={!!selectedImage}
        onClose={handleClose}
        maxWidth="lg"
        PaperProps={{
          sx: {
            backgroundColor: "transparent",
            boxShadow: "none",
          },
        }}
      >
        <IconButton
          data-testid="vlm-image-modal-close"
          onClick={handleClose}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: "white",
            backgroundColor: "rgba(0,0,0,0.5)",
            "&:hover": { backgroundColor: "rgba(0,0,0,0.7)" },
          }}
        >
          <CloseIcon />
        </IconButton>
        <DialogContent sx={{ p: 0, overflow: "hidden" }}>
          {selectedImage && (
            <img
              data-testid="vlm-image-modal-fullsize"
              src={selectedImage}
              alt="Full size"
              style={{
                maxWidth: "90vw",
                maxHeight: "90vh",
                objectFit: "contain",
              }}
            />
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ChatMessageImages;
